import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { useAutoPageTracking } from "./utils/analytics";

// Mini composant qui monte le tracker analytics (doit être à l'intérieur du Router
// car useAutoPageTracking utilise useLocation()).
function AnalyticsTracker() {
  useAutoPageTracking();
  return null;
}

// Route guards & layouts (extraits)
import { ProtectedRoute, ExpediteurRoute, ChauffeurRoute, AdminRoute } from "./routing/RouteGuards";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { AdminLayout } from "./layouts/AdminLayout";
import { DriverLayout } from "./layouts/DriverLayout";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import GoogleAuthSuccess from "./pages/GoogleAuthSuccess";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SetPassword from "./pages/SetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import ClientDashboard from "./pages/ClientDashboard";
import ClientOperations from "./pages/ClientOperations";
import ClientAnalytics from "./pages/ClientAnalytics";
import ClientAlerts from "./pages/ClientAlerts";
import ClientDocuments from "./pages/ClientDocuments";
import ClientFeedback from "./pages/ClientFeedback";
import TransportRequestPage from "./pages/TransportRequestPage";
import MonCompte from "./pages/MonCompte";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import FinanceAuditPage from "./pages/admin/FinanceAuditPage";
import FinanceCockpitPage from "./pages/admin/FinanceCockpitPage";
import ReconciliationWavePage from "./pages/admin/ReconciliationWavePage";
import WavePaymentsPage from "./pages/admin/WavePaymentsPage";
import WavePaymentLinksPage from "./pages/admin/WavePaymentLinksPage";
import WaveSandboxPayPage from "./pages/WaveSandboxPayPage";
import BankReconciliationPage from "./pages/admin/BankReconciliationPage";
import RapprochementUnifiePage from "./pages/admin/RapprochementUnifiePage";
import TreasureGlobalePage from "./pages/admin/TreasureGlobalePage";
import NpsCockpitPage from "./pages/admin/NpsCockpitPage";
import CustomerSuccessPage from "./pages/admin/CustomerSuccessPage";
import UserManagement from "./pages/admin/UserManagement";
import OperationsManagement from "./pages/admin/OperationsManagement";
import TrashOperations from "./pages/admin/TrashOperations";
import UsersManagement from "./pages/admin/UsersManagement";
import AuditLogs from "./pages/admin/AuditLogs";
import SecurityIncidentsPage from "./pages/admin/SecurityIncidentsPage";
import ClientsManagement from "./pages/admin/ClientsManagement";
import ClientCompletenessPage from "./pages/admin/ClientCompletenessPage";
import CompaniesAdminPage from "./pages/admin/CompaniesAdminPage";
import ClientsHubPage from "./pages/admin/ClientsHubPage";
import InternalUsersManagement from "./pages/admin/InternalUsersManagement";
import RoleManagement from "./pages/admin/RolesManagement";
import UnifiedUsersManagement from "./pages/admin/UnifiedUsersManagement";
import AdminOperationDetail from "./pages/admin/AdminOperationDetail";
import TransferDetail from "./pages/admin/TransferDetail";
import AdminLoadingNotesList from "./pages/admin/AdminLoadingNotesList";
import AdminTransferModificationRequests from "./pages/admin/AdminTransferModificationRequests";
import DriversManagement from "./pages/admin/DriversManagement";
import VehiclesManagement from "./pages/admin/VehiclesManagement";
import DriverForm from "./pages/admin/DriverForm";
import VehicleForm from "./pages/admin/VehicleForm";
import VehicleDetail from "./pages/admin/VehicleDetail";
import MessagingCenterV2 from "./pages/MessagingCenterV2";
import ClientMessagingPro from "./pages/ClientMessagingPro";
import ClientFinances from "./pages/ClientFinances";
import ClientContactsSettings from "./pages/ClientContactsSettings";
import AdminCompanyStampSettings from "./pages/AdminCompanyStampSettings";
import ClientLoadingNotesList from "./pages/ClientLoadingNotesList";
import ClientIncidents from "./pages/ClientIncidents";
import SystemConfiguration from "./pages/admin/SystemConfiguration";
import PaymentPoliciesManagement from "./pages/admin/PaymentPoliciesManagement";
import SettingsPage from "./pages/admin/SettingsPage";
import StaffProfile from "./pages/admin/StaffProfile";
import FneSettings from "./pages/admin/FneSettings";
import FneInvoices from "./pages/admin/FneInvoices";
import CommercialDashboardPage from "./pages/admin/CommercialDashboardPage";
import MyOverduePage from "./pages/admin/MyOverduePage";
import OpsTeamWorkload from "./pages/admin/OpsTeamWorkload";
import FneApiLogs from "./pages/admin/FneApiLogs";
import TrackingPage from "./pages/TrackingPage";
import PendingDriversManagement from "./pages/admin/PendingDriversManagement";
import NegotiationsPage from "./pages/admin/NegotiationsPage";
import NotificationSettings from "./pages/NotificationSettings";
import NotificationTemplatesAdmin from "./pages/admin/NotificationTemplatesAdmin";
import IncidentsManagement from "./pages/admin/IncidentsManagement";
import CreateIncidentPage from "./pages/admin/CreateIncidentPage";
import TemplatesManagement from "./pages/admin/TemplatesManagement";
import TransportPartners from "./pages/admin/TransportPartners";
import MCPDashboard from "./pages/admin/MCPDashboard";
import DocumentationPage from "./pages/admin/DocumentationPage";

// Contracts Pages
import ContractsList from "./pages/admin/contracts/ContractsList";
import ContractCreate from "./pages/admin/contracts/ContractCreate";
import ContractDetail from "./pages/admin/contracts/ContractDetail";
import ContractEdit from "./pages/admin/contracts/ContractEdit";
import ContractRunsList from "./pages/admin/contracts/ContractRunsList";

// Driver Pages
import DriverDashboard from "./pages/driver/DriverDashboard";
import DriverMissions from "./pages/driver/DriverMissions";
import MissionDetail from "./pages/driver/MissionDetail";
import DriverVehicles from "./pages/driver/DriverVehicles";

// Shared Pages
import OperationDetail from "./pages/OperationDetail";

// Public Pages (no auth required)
import PublicTrackingPage from "./pages/PublicTrackingPage";
import PublicSignPage from "./pages/PublicSignPage";
import PublicClientGuidePage from "./pages/PublicClientGuidePage";
import PublicOnboardingPage from "./pages/PublicOnboardingPage";
import AdminGuidePage from "./pages/admin/AdminGuidePage";
import PublicAdminOnboardingPage from "./pages/admin/PublicAdminOnboardingPage";
import TrainingCatalogPage from "./pages/TrainingCatalogPage";
import TrainingModulePage from "./pages/TrainingModulePage";
import RbacAuditDashboard from "./pages/admin/RbacAuditDashboard";

// Client Portal Pages
import ClientContracts from "./pages/ClientContracts";
import ClientContractDetail from "./pages/ClientContractDetail";
import ClientTransferDetail from "./pages/ClientTransferDetail";
import ClientCalendar from "./pages/ClientCalendar";
import ReportsPage from "./pages/ReportsPage";
import { WorkflowDivergencesDashboard } from "./components/admin/WorkflowDivergencesDashboard";
import ReportConfigurationPage from "./pages/ReportConfigurationPage";

// Driver Profile
import DriverProfile from "./pages/driver/DriverProfile";

// Protected Route Component - vérifie l'authentification
// (Route guards et Layouts sont extraits dans ./routing/RouteGuards et ./layouts/*)

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <div className="App">
          <BrowserRouter>
          <AnalyticsTracker />
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/wave-sandbox-pay/:linkId" element={<WaveSandboxPayPage />} />
            <Route path="/inscription" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/google/callback" element={<GoogleAuthSuccess />} />
            <Route path="/auth/google/success" element={<GoogleAuthSuccess />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* Public Tracking Page - No Auth Required */}
            <Route path="/public/suivi" element={<PublicTrackingPage />} />
            <Route path="/public/suivi/:operationNumber" element={<PublicTrackingPage />} />

            {/* Public Sign Page - PM Spec 30/04/2026 - No Auth Required */}
            <Route path="/sign/:opId" element={<PublicSignPage />} />

            {/* Internal Documentation — Auth requise pour tous les guides */}
            <Route path="/docs/client" element={<ProtectedRoute><PublicClientGuidePage /></ProtectedRoute>} />
            <Route path="/guide" element={<ProtectedRoute><PublicClientGuidePage /></ProtectedRoute>} />
            <Route path="/docs/client/onboarding" element={<ProtectedRoute><PublicOnboardingPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><PublicOnboardingPage /></ProtectedRoute>} />

            {/* Internal Documentation — Guide Admin (auth + staff required) */}
            <Route path="/docs/admin" element={<ProtectedRoute><AdminGuidePage /></ProtectedRoute>} />
            <Route path="/docs/admin/onboarding" element={<ProtectedRoute><PublicAdminOnboardingPage /></ProtectedRoute>} />

            {/* Centre de formation par module — Client */}
            <Route path="/docs/client/training" element={<ProtectedRoute><TrainingCatalogPage population="client" /></ProtectedRoute>} />
            <Route path="/docs/client/training/:moduleId" element={<ProtectedRoute><TrainingModulePage population="client" /></ProtectedRoute>} />

            {/* Centre de formation par module — Staff interne */}
            <Route path="/docs/admin/training" element={<ProtectedRoute><TrainingCatalogPage population="staff" /></ProtectedRoute>} />
            <Route path="/docs/admin/training/:moduleId" element={<ProtectedRoute><TrainingModulePage population="staff" /></ProtectedRoute>} />
            
            {/* Admin Public Route */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />

            {/* Routes Expéditeur (Client) - PROTÉGÉES PAR RÔLE */}
            <Route
              path="/dashboard"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientDashboard />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/nouvelle-demande"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <TransportRequestPage />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/mon-compte"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <MonCompte />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/operations"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientOperations />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientAnalytics />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/mes-alertes"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientAlerts />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/mes-documents"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientDocuments />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/evaluations"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientFeedback />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/messagerie"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientMessagingPro />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/finances"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientFinances />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/parametres/contacts"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientContactsSettings />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/mes-bons"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientLoadingNotesList />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/mes-incidents"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientIncidents />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/suivi"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <TrackingPage />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            
            {/* Client Portal - Contracts Routes */}
            <Route
              path="/mes-contrats"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientContracts />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/mes-contrats/:id"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientContractDetail />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/mes-contrats/:contractId/transferts/:runId"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientTransferDetail />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/calendrier"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ClientCalendar />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/mes-notifications"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <NotificationSettings />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/mes-rapports"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ReportsPage />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/mes-rapports/configuration"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <ReportConfigurationPage />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />

            {/* Routes Admin - PROTÉGÉES PAR RÔLE ADMIN */}
            <Route
              path="/admin/dashboard"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/audit-factures"
              element={<Navigate to="/admin/finance/cockpit?tab=audit" replace />}
            />
            <Route
              path="/admin/finance-audit"
              element={<Navigate to="/admin/finance/cockpit?tab=audit" replace />}
            />
            <Route
              path="/admin/finance/cockpit"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <FinanceCockpitPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/rapprochement"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <RapprochementUnifiePage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/tresorerie-globale"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <TreasureGlobalePage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/rapprochement/wave"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <ReconciliationWavePage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/rapprochement/wave/paiements"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <WavePaymentsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/rapprochement/wave/liens"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <WavePaymentLinksPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/rapprochement/banque"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <BankReconciliationPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/finance/wave-payments"
              element={<Navigate to="/admin/rapprochement/wave/paiements" replace />}
            />
            <Route
              path="/admin/nps-cockpit"
              element={<Navigate to="/admin/customer-success?tab=nps" replace />}
            />
            <Route
              path="/admin/customer-success"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <CustomerSuccessPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <UserManagement />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/operations"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <OperationsManagement />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/operations/corbeille"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <TrashOperations />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/operations/:id"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminOperationDetail />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/transfers/:id"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <TransferDetail />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/loading-notes"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminLoadingNotesList />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/transfer-modification-requests"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminTransferModificationRequests />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users-management"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <UsersManagement />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={<Navigate to="/admin/settings?tab=audit" replace />}
            />
            <Route
              path="/admin/security-incidents"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <SecurityIncidentsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <ClientsHubPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/companies"
              element={<Navigate to="/admin/clients?tab=companies" replace />}
            />
            <Route
              path="/admin/clients/completeness"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <ClientCompletenessPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/internal-users"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <InternalUsersManagement />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/unified-users"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <UnifiedUsersManagement />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <AdminLayout>
                  <RoleManagement />
                </AdminLayout>
              }
            />
            <Route
              path="/admin/rbac-audit"
              element={
                <AdminLayout>
                  <RbacAuditDashboard />
                </AdminLayout>
              }
            />
            <Route
              path="/admin/tracking"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <TrackingPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/negotiations"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <NegotiationsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            
            {/* Reports Route */}
            <Route
              path="/admin/rapports"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <ReportsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />

            {/* Workflow Divergences Analytics (Phase 2 Observe & Learn) */}
            <Route
              path="/admin/analytics/divergences"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <WorkflowDivergencesDashboard />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            
            {/* Contracts Routes */}
            <Route
              path="/admin/contracts"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <ContractsList />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contracts/new"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <ContractCreate />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contracts/:id"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <ContractDetail />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contracts/:id/edit"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <ContractEdit />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contracts/:contractId/runs"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <ContractRunsList />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contracts/:contractId/runs/:runId"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <TransferDetail />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contract-runs"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <ContractRunsList />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contract-runs/:id"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <TransferDetail />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            
            
            <Route
              path="/admin/drivers"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <DriversManagement />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/drivers/pending"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <PendingDriversManagement />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/drivers/new"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <DriverForm />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/drivers/:id/edit"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <DriverForm />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/vehicles"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <VehiclesManagement />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/vehicles/new"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <VehicleForm />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/vehicles/:id/edit"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <VehicleForm />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/vehicles/:id"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <VehicleDetail />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/messagerie"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <MessagingCenterV2 />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/incidents/new"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <CreateIncidentPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/incidents"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <IncidentsManagement />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/transport-partners"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <TransportPartners />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/system-config"
              element={<Navigate to="/admin/settings?tab=system" replace />}
            />
            <Route
              path="/admin/branding/stamp"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminCompanyStampSettings />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/notification-templates"
              element={<Navigate to="/admin/notifications/templates" replace />}
            />
            <Route
              path="/admin/messaging"
              element={<Navigate to="/admin/messagerie" replace />}
            />
            <Route
              path="/admin/billing"
              element={<Navigate to="/admin/settings?tab=payment" replace />}
            />
            <Route
              path="/admin/payment-policies"
              element={<Navigate to="/admin/settings?tab=payment" replace />}
            />
            <Route
              path="/admin/backup"
              element={<Navigate to="/admin/settings?tab=backup" replace />}
            />
            <Route
              path="/admin/settings"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <SettingsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/mon-profil"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <StaffProfile />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/settings/fne"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <FneSettings />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/commercial/dashboard"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <CommercialDashboardPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/commercial/overdue"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <MyOverduePage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/ops/team"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <OpsTeamWorkload />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/fne"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <FneInvoices />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/fne/api-logs"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <FneApiLogs />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/mcp"
              element={
                <AdminRoute allowedRoles={['admin']}>
                  <AdminLayout>
                    <MCPDashboard />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/documentation"
              element={<Navigate to="/docs/admin" replace />}
            />
            <Route
              path="/operations/:id"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <OperationDetail />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />
            <Route
              path="/notifications/settings"
              element={
                <ExpediteurRoute>
                  <DashboardLayout>
                    <NotificationSettings />
                  </DashboardLayout>
                </ExpediteurRoute>
              }
            />

            {/* Route Admin pour les templates de notification */}
            <Route
              path="/admin/notifications/templates"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <NotificationTemplatesAdmin />
                  </AdminLayout>
                </AdminRoute>
              }
            />

            {/* Route Admin pour les templates d'opérations et de contrats */}
            <Route
              path="/admin/templates"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <TemplatesManagement />
                  </AdminLayout>
                </AdminRoute>
              }
            />

            {/* Routes Chauffeur - PROTÉGÉES PAR RÔLE CHAUFFEUR */}
            <Route
              path="/chauffeur/dashboard"
              element={
                <ChauffeurRoute>
                  <DriverLayout>
                    <DriverDashboard />
                  </DriverLayout>
                </ChauffeurRoute>
              }
            />
            <Route
              path="/chauffeur/missions"
              element={
                <ChauffeurRoute>
                  <DriverLayout>
                    <DriverMissions />
                  </DriverLayout>
                </ChauffeurRoute>
              }
            />
            <Route
              path="/chauffeur/missions/:id"
              element={
                <ChauffeurRoute>
                  <DriverLayout>
                    <MissionDetail />
                  </DriverLayout>
                </ChauffeurRoute>
              }
            />
            <Route
              path="/chauffeur/mission-active"
              element={
                <ChauffeurRoute>
                  <DriverLayout>
                    <DriverMissions />
                  </DriverLayout>
                </ChauffeurRoute>
              }
            />
            <Route
              path="/chauffeur/vehicules"
              element={
                <ChauffeurRoute>
                  <DriverLayout>
                    <DriverVehicles />
                  </DriverLayout>
                </ChauffeurRoute>
              }
            />
            <Route
              path="/chauffeur/profil"
              element={
                <ChauffeurRoute>
                  <DriverLayout>
                    <DriverProfile />
                  </DriverLayout>
                </ChauffeurRoute>
              }
            />
            <Route
              path="/chauffeur"
              element={<Navigate to="/chauffeur/dashboard" replace />}
            />
          </Routes>
        </BrowserRouter>
        <Toaster />
        <SonnerToaster />
      </div>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
